import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, 
  Users, 
  Clock, 
  Activity,
  ArrowRight,
  PlayCircle,
  MessageSquare,
  TrendingUp,
  Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlanUsageWidget } from "@/components/PlanUsageWidget";

const API = `${process.env.REACT_APP_API_URL}/api`;

const SPEAKR_LOGO_BLACK = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/v0cvlfy5_Modern%20AI%20voice%20intelligence%20logo.png";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    total_calls: 0,
    total_agents: 0,
    active_agents: 0,
    total_minutes: 0,
    recent_calls_7d: 0,
    outcomes: {}
  });
  const [agents, setAgents] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [statsRes, agentsRes, callsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/agents`, { headers }),
        axios.get(`${API}/calls?limit=5`, { headers })
      ]);
      setStats(statsRes.data);
      setAgents(agentsRes.data);
      setRecentCalls(callsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: "Total Calls", 
      value: stats.total_calls, 
      icon: Phone, 
      color: "text-cyan-500",
      bg: "bg-cyan-500/10"
    },
    { 
      title: "Active Agents", 
      value: `${stats.active_agents}/${stats.total_agents}`, 
      icon: Users, 
      color: "text-emerald-500",
      bg: "bg-emerald-500/10"
    },
    { 
      title: "Talk Time", 
      value: `${stats.total_minutes}m`, 
      icon: Clock, 
      color: "text-amber-500",
      bg: "bg-amber-500/10"
    },
    { 
      title: "This Week", 
      value: stats.recent_calls_7d, 
      icon: TrendingUp, 
      color: "text-purple-500",
      bg: "bg-purple-500/10"
    },
  ];

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="dashboard-loading">
        <div className="loading-dots flex gap-1">
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce"></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">
          Welcome, {user?.name || "Business"}!
        </h1>
        <p className="text-muted-foreground">Your AI Voice Intelligence command center</p>
      </div>

      {/* Plan & Usage Widget + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        {/* Plan Usage Widget - Takes 1 column on large screens */}
        <div className="lg:col-span-1">
          <PlanUsageWidget />
        </div>
        
        {/* Stats Grid - Takes 3 columns */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          {statCards.map((stat, index) => (
            <Card 
              key={stat.title} 
              className="card-hover bg-card border-border"
              data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                    <p className="text-3xl font-heading font-bold">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Agents Section */}
        <Card className="lg:col-span-2 bg-card border-border" data-testid="agents-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading">Your AI Agents</CardTitle>
              <CardDescription>Click an agent to start a voice call</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard/agents')}
              data-testid="view-all-agents-btn"
            >
              Manage <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {agents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {agents.slice(0, 4).map((agent) => (
                  <div 
                    key={agent.id}
                    className={`flex items-center gap-4 p-4 rounded-xl transition-colors cursor-pointer group ${
                      agent.is_active 
                        ? "bg-secondary/50 hover:bg-secondary" 
                        : "bg-secondary/20 opacity-60"
                    }`}
                    onClick={() => agent.is_active && navigate(`/dashboard/call/${agent.id}`)}
                    data-testid={`agent-card-${agent.name.toLowerCase()}`}
                  >
                    <div className="relative">
                      <Avatar className="w-12 h-12 ring-2 ring-primary/20">
                        <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-purple-500 text-white">
                          {agent.name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-card ${
                        agent.is_active ? "bg-emerald-500" : "bg-gray-500"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{agent.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{agent.role}</p>
                    </div>
                    {agent.is_active && (
                      <Button 
                        size="icon" 
                        className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-gradient-to-r from-cyan-500 to-purple-500"
                        data-testid={`call-agent-${agent.name.toLowerCase()}-btn`}
                      >
                        <PlayCircle className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No agents yet. Let's create your first AI agent!</p>
                <Button 
                  className="mt-4" 
                  onClick={() => navigate('/dashboard/agents')}
                  data-testid="create-first-agent-btn"
                >
                  Create Agent
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Calls */}
        <Card className="bg-card border-border" data-testid="recent-calls-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading">Recent Calls</CardTitle>
              <CardDescription>Latest conversations</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard/history')}
              data-testid="view-all-history-btn"
            >
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {recentCalls.map((call) => (
                  <div 
                    key={call.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/dashboard/history')}
                    data-testid={`recent-call-${call.id}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${
                      call.status === 'active' ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{call.agent_name}</p>
                      <p className="text-xs text-muted-foreground">{formatTime(call.started_at)}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant={call.status === 'active' ? 'default' : 'secondary'}>
                        {call.status === 'active' ? 'Active' : `${Math.floor(call.duration_seconds / 60)}m`}
                      </Badge>
                      {call.outcome && (
                        <p className="text-xs text-emerald-500 mt-1">{call.outcome}</p>
                      )}
                    </div>
                  </div>
                ))}
                {recentCalls.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No calls yet</p>
                    <p className="text-xs">Start a call to see activity here</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Quick Action - Speakr Hero */}
      <Card className="mt-6 overflow-hidden border-0" data-testid="quick-action-card">
        <CardContent className="p-0">
          <div className="relative bg-gradient-to-r from-[#0a0a1a] via-[#0d1033] to-[#0a0a1a] p-8 flex items-center justify-between">
            {/* Background glow effects */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
            </div>
            
            <div className="relative flex items-center gap-8">
              <img 
                src={SPEAKR_LOGO_BLACK} 
                alt="Speakr" 
                className="w-32 h-auto object-contain hidden md:block"
              />
              <div>
                <h3 className="text-2xl font-heading font-bold mb-1">Ready to make a call?</h3>
                <p className="text-muted-foreground">Test your AI agents with a live voice conversation</p>
              </div>
            </div>
            <Button 
              size="lg" 
              className="relative rounded-full px-8 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-400 hover:via-blue-400 hover:to-purple-400 shadow-lg shadow-blue-500/25"
              onClick={() => navigate('/dashboard/call')}
              data-testid="start-call-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Start Call
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
